import { useCallback, useMemo } from 'react'
import { useUpdateAccountSettings } from './useAccountSettings'
import { useQuery } from '@tanstack/react-query'
import { accountQueries } from '@/services/accountApi'
import { useSettingsContext } from '@/contexts/SettingsContext'
import {
  getLocalSetting,
  setLocalSetting,
  removeLocalSetting,
  getSettingDefault,
} from '@/lib/settingsStorage'
import type { 
  SettingKey, 
  SettingValueTypes, 
  SettingState, 
  SettingPersistLevel 
} from '@/types/settings'

export interface UseSettingOptions {
  /** Whether to persist to account settings (server) */
  persistAccount?: boolean
  /** Whether to persist to localStorage */
  persistLocal?: boolean
  /** Whether to allow session override */
  persistSession?: boolean
}

export interface UseSettingResult<K extends SettingKey> {
  /** The effective value (session > local > account > default) */
  value: SettingValueTypes[K]
  /** Where the effective value came from */
  source: 'session' | 'local' | 'account' | 'default'

  /** The account default value (from server) */
  accountValue: SettingValueTypes[K]
  /** The localStorage override value */
  localValue: SettingValueTypes[K] | null
  /** The session override value */
  sessionValue: SettingValueTypes[K] | undefined

  /** Whether account settings are loading */
  isLoading: boolean
  /** Whether account settings are being updated */
  isUpdating: boolean

  /** Set the account default (saves to server) - use in settings pages */
  setAccountDefault: (value: SettingValueTypes[K]) => Promise<void>
  /** Set a localStorage override - persists across tabs */
  setLocalOverride: (value: SettingValueTypes[K]) => void
  /** Set a session override - only for current tab */
  setSessionOverride: (value: SettingValueTypes[K]) => void
  
  /** Clear the localStorage override */
  resetLocal: () => void
  /** Clear the session override */
  resetSession: () => void
  /** Get full setting state with all values and sources */
  getSettingState: () => SettingState<SettingValueTypes[K]>
}

/**
 * Universal setting hook that manages account defaults, localStorage, and session overrides
 * 
 * Precedence: session > local > account > default
 * 
 * @example
 * // In settings page - set account default
 * const { value, setAccountDefault } = useSetting('call:caller_id')
 * // onChange: setAccountDefault(newValue)
 * 
 * @example
 * // In dialer - set session override
 * const { value, setSessionOverride } = useSetting('call:caller_id')
 * // onChange: setSessionOverride(newValue)
 */
export function useSetting<K extends SettingKey>(
  key: K,
  options: UseSettingOptions = {}
): UseSettingResult<K> {
  const {
    persistAccount = true,
    persistLocal = true,
    persistSession = true,
  } = options

  // Get account settings from server
  const { data: accountSettingsData, isLoading } = useQuery(accountQueries.settings())
  const updateMutation = useUpdateAccountSettings()

  // Get session context
  const { 
    getSessionSetting, 
    setSessionSetting, 
    clearSessionSetting 
  } = useSettingsContext()

  // Get default value
  const defaultValue = getSettingDefault(key)

  // Get account value
  const accountValue = useMemo(() => {
    if (!persistAccount) return defaultValue
    return (accountSettingsData?.settings?.[key] as SettingValueTypes[K]) ?? defaultValue
  }, [accountSettingsData?.settings, key, defaultValue, persistAccount])

  // Get local value
  const localValue = useMemo(() => {
    if (!persistLocal) return null
    return getLocalSetting(key)
  }, [key, persistLocal])

  // Get session value
  const sessionValue = useMemo(() => {
    if (!persistSession) return undefined
    return getSessionSetting(key)
  }, [key, persistSession, getSessionSetting])

  // Compute effective value (session > local > account > default)
  const { value, source } = useMemo(() => {
    if (sessionValue !== undefined) {
      return { value: sessionValue, source: 'session' as const }
    }
    if (localValue !== null) {
      return { value: localValue, source: 'local' as const }
    }
    if (accountValue !== defaultValue || persistAccount) {
      return { value: accountValue, source: 'account' as const }
    }
    return { value: defaultValue, source: 'default' as const }
  }, [sessionValue, localValue, accountValue, defaultValue, persistAccount])

  // Set account default (saves to server)
  const setAccountDefault = useCallback(async (newValue: SettingValueTypes[K]) => {
    if (!persistAccount) {
      throw new Error(`Setting ${key} does not support account persistence`)
    }
    await updateMutation.mutateAsync({ [key]: newValue })
  }, [key, persistAccount, updateMutation])

  // Set local override
  const setLocalOverride = useCallback((newValue: SettingValueTypes[K]) => {
    if (!persistLocal) {
      throw new Error(`Setting ${key} does not support local persistence`)
    }
    setLocalSetting(key, newValue)
  }, [key, persistLocal])

  // Set session override
  const setSessionOverride = useCallback((newValue: SettingValueTypes[K]) => {
    if (!persistSession) {
      throw new Error(`Setting ${key} does not support session persistence`)
    }
    setSessionSetting(key, newValue)
  }, [key, persistSession, setSessionSetting])

  // Reset local override
  const resetLocal = useCallback(() => {
    if (persistLocal) {
      removeLocalSetting(key)
    }
  }, [key, persistLocal])

  // Reset session override
  const resetSession = useCallback(() => {
    if (persistSession) {
      clearSessionSetting(key)
    }
  }, [key, persistSession, clearSessionSetting])

  // Get full setting state
  const getSettingState = useCallback((): SettingState<SettingValueTypes[K]> => ({
    value,
    source,
    accountValue,
    localValue: localValue ?? undefined,
    sessionValue,
  }), [value, source, accountValue, localValue, sessionValue])

  return {
    value,
    source,
    accountValue,
    localValue,
    sessionValue,
    isLoading,
    isUpdating: updateMutation.isPending,
    setAccountDefault,
    setLocalOverride,
    setSessionOverride,
    resetLocal,
    resetSession,
    getSettingState,
  }
}

/**
 * Convenience hook for settings that only need account defaults
 * Use this in settings pages
 */
export function useAccountSetting<K extends SettingKey>(
  key: K
): Pick<UseSettingResult<K>, 'value' | 'accountValue' | 'setAccountDefault' | 'source' | 'isLoading'> {
  const setting = useSetting(key, { 
    persistAccount: true, 
    persistLocal: false, 
    persistSession: false 
  })
  
  return {
    value: setting.value,
    accountValue: setting.accountValue,
    setAccountDefault: setting.setAccountDefault,
    source: setting.source,
    isLoading: setting.isLoading,
  }
}

/**
 * Convenience hook for settings that support session overrides
 * Use this in feature UIs like the dialer
 */
export function useSessionSetting<K extends SettingKey>(
  key: K
): Pick<UseSettingResult<K>, 
  'value' | 'accountValue' | 'localValue' | 'sessionValue' | 
  'setSessionOverride' | 'resetSession' | 'source'
> {
  const setting = useSetting(key, { 
    persistAccount: true, 
    persistLocal: true, 
    persistSession: true 
  })
  
  return {
    value: setting.value,
    accountValue: setting.accountValue,
    localValue: setting.localValue,
    sessionValue: setting.sessionValue,
    setSessionOverride: setting.setSessionOverride,
    resetSession: setting.resetSession,
    source: setting.source,
  }
}
